import React, { useEffect, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Box, Heading, Text, VStack, Spinner, HStack, Button } from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks/hooks";
import { RootState } from "../../../store/store";
import { fetchOneDelivery } from "../../../store/deliverySlice/deliverySlice";
import DriverSimulatorOnRoute from "../RealTime/DriverSimulatorOnRoute";

export default function DriverSimulatorPage() {
    const { deliveryId } = useParams<{ deliveryId: string; }>();
    const dispatch = useAppDispatch();

    const fromList = useAppSelector((s: RootState) =>
        s.deliveryRequests.deliveries.find((d: any) => d._id === deliveryId)
    );
    const current = useAppSelector((s: RootState) => s.deliveryRequests.currentDelivery);
    const loading = useAppSelector((s: RootState) => s.deliveryRequests.loading);
    const error = useAppSelector((s: RootState) => s.deliveryRequests.error);

    useEffect(() => {
        if (!deliveryId) return;
        if (!fromList && (!current || current._id !== deliveryId)) {
            dispatch(fetchOneDelivery(deliveryId));
        }
    }, [deliveryId, fromList, current, dispatch]);

    const delivery = useMemo(() => {
        if (fromList) return fromList;
        if (current && current._id === deliveryId) return current;
        return undefined;
    }, [fromList, current, deliveryId]);

    if (!deliveryId) return <Navigate to="/delivery/list" replace />;

    if (error) {
        return (
            <Box p={4}>
                <Text color="red.500">Failed to load delivery. {error}</Text>
                <Button mt={3} onClick={() => dispatch(fetchOneDelivery(deliveryId))}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (!delivery || loading) {
        return (
            <Box p={6}>
                <HStack>
                    <Spinner />
                    <Text>Loading delivery…</Text>
                </HStack>
            </Box>
        );
    }

    const pickup = { lat: delivery.pickup.lat, lng: delivery.pickup.lng };
    const dropoff = { lat: delivery.dropoff.lat, lng: delivery.dropoff.lng };

    return (
        <Box p={4}>
            <Heading size="md" mb={3}>Driver Simulator (Route-aware)</Heading>
            <VStack align="start" spacing={1} mb={3}>
                <Text><strong>Trip:</strong> {deliveryId}</Text>
                <Text><strong>From:</strong> {delivery.pickup.address}</Text>
                <Text><strong>To:</strong> {delivery.dropoff.address}</Text>
            </VStack>

            <DriverSimulatorOnRoute
                tripId={deliveryId}
                pickup={pickup}
                dropoff={dropoff}
                startKph={35}
            />
        </Box>
    );
}
